import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { SessionDataResult } from '../../interfaces/sessions';
import { getServicesManager } from '../../services';
import { TranscriptHeader } from '../SessionDetails/TranscriptView/TranscriptHeader';
import { TranscriptBody } from '../SessionDetails/TranscriptView/TranscriptBody';
import { TranscriptAudio } from '../SessionDetails/TranscriptView';
import { SessionDetailView } from '../History/views';
import { Loader } from '../Loader';
import { Typography } from '../../interfaces/typography';
import { AlertTriangle } from 'lucide-react';
import { SEC_TO_MS } from '../../cfg/const';

export const PublicSessionView: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [sessionData, setSessionData] = useState<SessionDataResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Audio player state (same as regular UI)
  const [selectedTranscriptIdx, setSelectedTranscriptIdx] = useState<number | undefined>();
  const [currentTimeInMs, setCurrentTimeInMs] = useState(0);
  const currentTimeInMsRef = useRef(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [previousPlayState, setPreviousPlayState] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const sessionAccessToken = searchParams.get('access_token');
  const sessionId = searchParams.get('session_id');

  // Calculate total duration (same as regular UI)
  const totalDurationInMs = useMemo(() => {
    return (sessionData?.call_metric?.call_duration ?? 0) * SEC_TO_MS;
  }, [sessionData]);

  // Handle transcript selection (same as regular UI)
  useEffect(() => {
    if (selectedTranscriptIdx !== undefined && sessionData && sessionData.turns.length > selectedTranscriptIdx) {
      const transcript = sessionData?.turns[selectedTranscriptIdx];
      if (transcript) { 
        const timeInMs = transcript.start * SEC_TO_MS;
        setCurrentTimeInMs(timeInMs);
        currentTimeInMsRef.current = timeInMs;
      }
    }
  }, [selectedTranscriptIdx, sessionData]);

  // Reset playback state when session data changes
  useEffect(() => {
    currentTimeInMsRef.current = 0;
    setCurrentTimeInMs(0);
    setIsPlaying(false);
    setSelectedTranscriptIdx(undefined);
  }, [sessionId]);

  // Timer effect for updating current time (same as regular UI)
  useEffect(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    if (isPlaying) {
      timerRef.current = setInterval(() => {
        const newTime = currentTimeInMsRef.current + (speed * 250);
        if (newTime > totalDurationInMs) {
          setIsPlaying(false);
        }
        const clampedTime = Math.min(newTime, totalDurationInMs);
        currentTimeInMsRef.current = clampedTime;
        setCurrentTimeInMs(clampedTime);
      }, 250);
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isPlaying, speed, totalDurationInMs]);

  // Handle time updates (same as regular UI)
  const handleUpdateTime = useCallback((time?: number, isActive?: boolean, prevActive?: boolean) => {
    if (time !== undefined) {
      currentTimeInMsRef.current = time;
      setCurrentTimeInMs(time);
    }
    setIsPlaying(isActive ?? false);
    setPreviousPlayState(prevActive ?? false);
  }, []);

  useEffect(() => {
    const loadSessionData = async () => {
      if (!sessionAccessToken || !sessionId) {
        setError('Missing required parameters');
        setLoading(false);
        return;
      }

      try {
        const servicesManager = getServicesManager();
        const data = await servicesManager.getSessionDataViaAccessToken(sessionAccessToken, sessionId);
        
        if (data) {
          setSessionData(data);
        } else {
          setError('Session not found or access token invalid');
        }
      } catch (err) {
        console.error('Failed to load session data:', err);
        setError('Failed to load session data');
      } finally {
        setLoading(false);
      }
    };

    loadSessionData();
  }, [sessionAccessToken, sessionId]);

  const handleGetTrellus = () => {
    window.open('https://trellus.ai', '_blank');
  };

  if (loading) {
    return (
      <div className="w-full h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader />
          <Typography variant="mediumParagraph" color="gray" className="mt-4">
            Loading session...
          </Typography>
        </div>
      </div>
    );
  }

  if (error || !sessionData) {
    return (
      <div className="w-full h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8 text-center border border-gray-200">
          <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <Typography variant="h2" className="mb-3">
            Access Denied
          </Typography>
          <Typography variant="mediumParagraph" color="gray" className="mb-6">
            {error || 'Unable to access this session. The link may be invalid or expired.'}
          </Typography>
          <button
            onClick={handleGetTrellus}
            className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
          >
            Go to Trellus
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-screen flex flex-col bg-white">
      {/* Trellus branding header */}
      <div className="bg-white border-b border-gray-200 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <svg 
              height="28" 
              viewBox="0 0 40 50" 
              fill="none" 
              xmlns="http://www.w3.org/2000/svg"
              className="text-green-600"
              aria-hidden="true"
            >
              <path fillRule="evenodd" clipRule="evenodd" d="M26.2535 16.0368C26.2548 17.2236 26.2535 18.3853 26.2535 19.5721C28.7916 19.85 31.3296 20.1382 33.8677 20.4157C33.8698 22.7226 33.8677 21.2268 33.872 23.8423C33.8724 24.6006 33.3093 25.3524 32.4476 25.3325C29.8827 25.2658 27.3161 25.2104 24.7503 25.1507C24.7387 21.0536 24.7425 16.9557 24.7378 12.8591C24.7365 9.47312 21.4665 6.47671 18.2096 5.51888C12.1237 3.7283 6.08501 1.79317 0 0C0 2.93538 0 5.87119 0 8.80657C0 14.1692 5.67556 17.4474 10.7655 18.0126C14.0433 18.3762 17.3275 18.5909 20.6048 18.9458C20.6061 20.9826 20.6083 23.0186 20.6091 25.055C19.3241 25.0256 18.0395 24.9962 16.7553 24.9663C12.7803 24.8707 8.37332 27.5559 8.37419 31.5906C8.37722 37.4471 8.38284 43.304 8.38284 49.1609C11.6675 48.1979 14.9535 47.2323 18.2377 46.2671C21.4847 45.3149 24.7646 42.3085 24.7607 38.9325C24.7603 37.2125 24.7573 35.164 24.756 33.4444C24.7555 32.4121 24.7547 31.3798 24.7547 30.3484C23.374 30.4168 21.9924 30.3796 20.6117 30.4475C20.6117 33.2202 20.6173 36.3235 20.6173 39.0966C20.6173 40.5262 19.5578 42.0029 18.0408 42.376C16.654 42.718 15.2677 43.059 13.8818 43.4005C13.8814 39.3688 13.8758 35.3362 13.874 31.305C13.8732 29.9264 15.4053 28.9781 16.7562 28.9482C21.9868 28.8201 27.2191 28.6938 32.4498 28.5674C35.0294 28.5072 36.7836 26.2998 36.7801 23.9708C36.7771 21.8833 36.7767 19.7937 36.7715 17.7062C33.2656 17.1522 29.7602 16.5895 26.2535 16.0368ZM10.8646 13.5953C8.57458 13.2326 6.99393 12.086 6.99393 9.77479C6.99393 8.74814 6.99349 7.72193 6.99349 6.69615C10.6681 7.60117 14.341 8.50879 18.0148 9.41425C19.2557 9.72026 20.5979 11.0135 20.5983 12.2981C20.6018 13.3005 20.6018 14.1385 20.6026 15.14C17.3569 14.625 14.1103 14.1099 10.8646 13.5953Z" fill="currentColor"/>
            </svg>
            <Typography variant="h3" fontWeight="600" color="#059669">
              Trellus
            </Typography>
          </div>
          <button
            onClick={handleGetTrellus}
            className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
          >
            Learn more
          </button>
        </div>
      </div>

      {/* Session Header (same as regular UI) */}
      <TranscriptHeader 
        data={sessionData} 
        view={SessionDetailView.PUBLIC}
      />
      
      {/* Main content (same as regular UI) */}
      <div className="flex-grow overflow-auto flex">
        <TranscriptBody 
          view={SessionDetailView.PUBLIC}
          data={sessionData}
          currentTimeInMs={currentTimeInMs}
          isPlaying={isPlaying}
          updateSelectedTranscript={(idx) => {
            setSelectedTranscriptIdx(idx);
            if (isPlaying) {
              setIsPlaying(false);
            }
          }}
          selectedTranscriptIdx={selectedTranscriptIdx}
        />
      </div>

      {/* Audio Player (same as regular UI) */}
      <TranscriptAudio
        data={sessionData}
        currentTimeInMs={currentTimeInMs}
        isPlaying={isPlaying}
        updateTime={handleUpdateTime}
        setSpeed={setSpeed}
        speed={speed}
        previousPlayState={previousPlayState}
        totalDuration={totalDurationInMs}
      />
    </div>
  );
};